import { PaymentsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  PayBankingPaymentDocument,
  PaymentsDataCollectionDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useBankingAuthorization } from './useBankingAuthorization'

const MISSING_AUTHORIZATION_ERROR = 'Missing authorization'

export const usePayBankingPayment = () => {
  const [payBankingPayment, { loading }] = useMutation(PayBankingPaymentDocument, {
    extractorFn: (data) => data.bankingMutations.payBankingPayment,
    refetchQueries: [PaymentsDataTableDocument, PaymentsDataCollectionDocument],
    awaitRefetchQueries: true,
  })
  const { getAuthorizationUrl, loading: authLoading } = useBankingAuthorization()

  const payPayment = async (paymentId: number, employeeId: number, redirectUrl: string) => {
    const result = await payBankingPayment({
      variables: {
        id: paymentId,
      },
    })
    const handled = handleResult(result, () => {})
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('banking.payments.paid'),
      })

      // Get consentUrl from the last payment execution
      const paymentExecutions = handled.data.payment?.paymentExecutions ?? []
      const lastExecution = paymentExecutions[paymentExecutions.length - 1]
      const consentUrl = lastExecution?.consent?.consentUrl || undefined

      return {
        ...handled,
        consentUrl,
        authorizationUrl: undefined,
      }
    } else {
      // Check if error is due to missing authorization
      const isMissingAuthorization = handled.errors.some((error) => {
        return (
          'messages' in error &&
          error.messages?.some((message) => message.includes(MISSING_AUTHORIZATION_ERROR))
        )
      })

      if (isMissingAuthorization) {
        // Get authorization URL for the user to authorize with Swan
        const authorizationUrl = await getAuthorizationUrl(employeeId, redirectUrl)

        if (authorizationUrl) {
          return {
            ...handled,
            consentUrl: undefined,
            authorizationUrl,
          }
        }
      }

      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
      return {
        ...handled,
        consentUrl: undefined,
        authorizationUrl: undefined,
      }
    }
  }

  return {
    payPayment,
    loading: loading || authLoading,
  }
}
